import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Brain, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ImpactAnalyzer = ({ project }) => {
  const [impacts, setImpacts] = useState([]);
  const [simplificationItems, setSimplificationItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('impacts');
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisResults, setAnalysisResults] = useState({});

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [impactsRes, simplificationRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/impact-analysis`),
        axios.get(`${API}/projects/${project.id}/simplification-items`)
      ]);
      setImpacts(impactsRes.data);
      setSimplificationItems(simplificationRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeSimplification = async (item) => {
    setAnalyzing(item.id);
    try {
      const response = await axios.post(`${API}/ai/analyze-simplification`, {
        project_id: project.id,
        analysis_type: 'simplification',
        context: item
      });
      
      setAnalysisResults({
        ...analysisResults,
        [item.id]: response.data.result.ai_analysis
      });
    } catch (error) {
      console.error('Error analyzing simplification:', error);
      alert('Failed to analyze simplification item');
    } finally {
      setAnalyzing(null);
    }
  };

  const severityColors = {
    low: 'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    critical: 'bg-red-100 text-red-800 border-red-200'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Impact Analyzer</h2>
        <p className="text-gray-600 mb-6">Comprehensive impact assessment for your migration</p>

        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('impacts')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'impacts'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Impact Analyses ({impacts.length})
          </button>
          <button
            onClick={() => setActiveTab('simplification')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'simplification'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Simplification Items ({simplificationItems.length})
          </button>
        </div>

        {activeTab === 'impacts' && (
          <div className="space-y-4">
            {impacts.map((impact) => (
              <div key={impact.id} className={`border-2 rounded-lg p-4 ${severityColors[impact.severity]}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{impact.title}</h3>
                    <p className="text-sm text-gray-700 mt-1">{impact.description}</p>
                  </div>
                  <span className="px-3 py-1 bg-white rounded-full text-xs font-medium capitalize">
                    {impact.severity}
                  </span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3 text-sm">
                  <div>
                    <span className="text-gray-700 font-medium">Type: </span>
                    <span className="capitalize">{impact.analysis_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-700 font-medium">Effort: </span>
                    <span>{impact.effort_estimate_days} days</span>
                  </div>
                  <div>
                    <span className="text-gray-700 font-medium">Cost: </span>
                    <span>${impact.cost_estimate_usd.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-700 font-medium">Risk Score: </span>
                    <span>{impact.risk_score}/10</span>
                  </div>
                </div>

                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-700">Affected Components: </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {impact.affected_components.map((comp, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white rounded text-xs">{comp}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-700">Recommended Actions:</span>
                  <ul className="mt-1 space-y-1">
                    {impact.recommended_actions.map((action, idx) => (
                      <li key={idx} className="text-sm ml-4 list-disc">{action}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'simplification' && (
          <div className="space-y-4">
            {simplificationItems.map((item) => {
              const hasAnalysis = analysisResults[item.id];
              
              return (
                <div key={item.id} className="border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-mono bg-gray-100 px-3 py-1.5 rounded font-bold">{item.item_number}</span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-semibold">
                          {item.module}
                        </span>
                        <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${severityColors[item.impact_level]}`}>
                          {item.impact_level}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{item.description}</p>

                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-3 text-sm">
                        <div>
                          <span className="text-gray-600">Effort: </span>
                          <span className="font-bold">{item.estimated_effort_days} days</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Remediation: </span>
                          <span className="font-bold">{item.remediation_required ? 'Required' : 'Not Required'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Status: </span>
                          <span className="font-bold capitalize">{item.status}</span>
                        </div>
                      </div>

                      <div className="text-sm">
                        <span className="text-gray-600 font-medium">Affected Transactions: </span>
                        <span className="text-gray-900">{item.affected_transactions.join(', ')}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => analyzeSimplification(item)}
                      disabled={analyzing === item.id}
                      className="ml-4 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
                    >
                      {analyzing === item.id ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Analyzing...</span>
                        </>
                      ) : hasAnalysis ? (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Re-analyze</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          <span>AI Analyze</span>
                        </>
                      )}
                    </button>
                  </div>

                  {hasAnalysis && (
                    <div className="mt-6 pt-6 border-t-2 border-gray-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <Brain className="w-6 h-6 text-purple-600" />
                        <h4 className="text-lg font-bold text-gray-900">AI Analysis Results</h4>
                      </div>
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                          {analysisResults[item.id]}
                        </pre>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-sm text-green-700">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-semibold">Analysis Complete</span>
                        </div>
                        <button
                          onClick={() => {
                            const updatedResults = { ...analysisResults };
                            delete updatedResults[item.id];
                            setAnalysisResults(updatedResults);
                          }}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                        >
                          Clear Analysis
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
